import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';
import { saveUserMetadata } from 'src/app/modules/auth/core/_requests';
import { asyncWrapper } from 'src/app/modules/utils/AppFunction';
import { localStorageItem } from 'src/app/modules/utils/LocalStorage';

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirm: (signatureBase64: string) => void;
};

const SignatureModal = ({
	open,
	onClose,
	onConfirm,
}: Props) => {
	const [isDrawing, setIsDrawing] = useState(false);
	const [isEmpty, setIsEmpty] = useState(true);

	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		canvas.width = 500;
		canvas.height = 250;

		ctx.fillStyle = '#fff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
	}, [canvasRef]);

	useEffect(() => {
		drawExistConfigSignature();
	}, []);

	const drawExistConfigSignature = () => {
		const savedSignature = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA)?.field7;
		
		if (savedSignature && canvasRef.current) {
			const context = canvasRef.current.getContext('2d');
			const image = new Image();
			image.src = savedSignature;
			image.addEventListener('load', () => {
				context && context.drawImage(image, 0, 0);
			});
			setIsEmpty(false);
		}
	};

	const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.strokeStyle = '#000';
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';

		ctx.beginPath();
		ctx.moveTo(x, y);
		setIsDrawing(true);
		setIsEmpty(false);
	};

	const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!isDrawing) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.lineTo(x, y);
		ctx.stroke();
	};

	const stopDrawing = () => {
		setIsDrawing(false);
	};

	const confirmSignature = () => {
		onConfirm(getCanvasData());
	};

	const clearCanvas = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.fillStyle = '#fff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		setIsEmpty(true);
	};

	const getCanvasData = () => {
		const canvas = canvasRef.current;
		if (!canvas) return '';
		
		return canvas.toDataURL();
	};

	const saveSignatureToConfig = async () => {
		const [data] = await asyncWrapper(
			saveUserMetadata({
				field7: getCanvasData(),
			})
		);
		if (data) {
			toast.success('Lưu cấu hình chữ ký thành công');
		}
	};

	const useExistConfigSignature = () => {
		clearCanvas();
		drawExistConfigSignature();	
	};

	return (
		<Modal show={open} onHide={onClose} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="bi bi-pen me-2"></i>
					Ký tài liệu điện tử
				</Modal.Title>
			</Modal.Header>

			<Modal.Body className="p-4">
				<div className="text-center">
					<div className="alert alert-info mb-4">
						<i className="bi bi-info-circle me-2"></i>
						Vui lòng ký vào khung bên dưới. Chữ ký của bạn sẽ được đặt tại vị trí đã chỉ
						định trong tài liệu.
					</div>

					<div
						className="signature-canvas-container mb-3"
						style={{
							border: '3px dashed #007bff',
							borderRadius: '8px',
							padding: '10px',
							backgroundColor: '#f8f9fa',
						}}
					>
						<canvas
							ref={canvasRef}
							onMouseDown={startDrawing}
							onMouseMove={draw}
							onMouseUp={stopDrawing}
							onMouseLeave={stopDrawing}
							style={{
								cursor: 'crosshair',
								backgroundColor: '#fff',
								display: 'block',
								margin: '0 auto',
							}}
							width="500"
							height="250"
						/>
					</div>

					<div className="d-flex justify-content-center gap-2">
						<Button variant="outline-secondary" size="sm" onClick={clearCanvas}>
							<i className="bi bi-eraser me-1"></i>
							Xóa và vẽ lại
						</Button>
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={saveSignatureToConfig}
							disabled={isEmpty}
						>
							<i className="bi bi-save me-1"></i>
							Lưu vào cấu hình
						</Button>
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={useExistConfigSignature}
						>
							<i className="bi bi-image me-1"></i>
							Dùng chữ ký đã lưu
						</Button>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer className="gap-2">
				<Button variant="danger" onClick={onClose}>
					<i className="bi bi-x-circle me-1"></i>
					Hủy bỏ
				</Button>
				<Button onClick={confirmSignature}>
					<i className="bi bi-check-circle me-1"></i>
					Xác nhận ký
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default SignatureModal;
